import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { format, subMonths } from 'date-fns';
import { Observable, switchMap } from 'rxjs';

import { environment } from '../../../../../../environments/environment';
import { CountryService } from '../../../services/country.service';
import { MarketAnalysisResult } from '../../../drugs/models/drug-analysis.model';
import { DrugSearchLevel } from '../../../drugs/models/drug-grouping-level.model';
import { LinkKey, LinkStateUpdate } from '../../../linking/models/link-pair.model';
import { CompetitorChartData, CompetitorSubMarket } from '../../models/competitor-chart-data.model';
import { Competitor } from '../../models/competitor.model';
import { UpdateHospitalProductAnalysisSettings } from '../../models/hospital-product-analysis-settings.model';
import { PriceDevelopmentChartData } from '../../models/price-development-chart-data.model';
import { UpdateProductAnalysisSettings } from '../../models/product-analysis-settings.model';
import { PurchaseProduct, PurchaseProductTestResult } from '../../models/purchase-product.model';
import * as ProductOverviewActions from './product-overview.actions';
import * as ProductOverviewSelectors from './product-overview.selectors';
import { CompetitorsTotals, MarketShares } from './product-overview.selectors';

// Price type and grouping level are fixed defaults until the charts get controls for them.
const DEFAULT_PRICE_TYPE_ID: number = 1;
// 1 = month (0 = unknown, 2 = 14 days).
const DEFAULT_PERIOD_TYPE: number = 1;
// Currency all purchase product prices and margins are converted to.
const DEFAULT_CURRENCY: string = 'DKK';
// Each sub-market has its own purchase products endpoint.
const PURCHASE_PRODUCTS_PATHS: Record<CompetitorSubMarket, string> = {
  [CompetitorSubMarket.Primary]: 'Primary',
  [CompetitorSubMarket.Hospital]: 'Hospital',
};

export interface PurchaseTestParams {
  productId: string;
  productPrice: number;
  packSizeA: number;
  packSizeB: number;
  currency: string;
}

@Injectable({
  providedIn: 'root',
})
export class ProductOverviewService {
  private competitorsEndpoint = '/api/Competitors';
  private purchaseProductsEndpoint = '/api/PurchaseProducts';

  constructor(
    private http: HttpClient,
    private store: Store,
    private countryService: CountryService
  ) {}

  loadCompetitors(productId: string): void {
    this.store.dispatch(ProductOverviewActions.loadCompetitors({ productId }));
  }

  getCompetitors$(): Observable<Competitor[]> {
    return this.store.select(ProductOverviewSelectors.selectActiveCompetitors);
  }

  getDormants$(): Observable<Competitor[]> {
    return this.store.select(ProductOverviewSelectors.selectDormants);
  }

  getCompetitorsTotals$(): Observable<CompetitorsTotals> {
    return this.store.select(ProductOverviewSelectors.selectCompetitorsTotals);
  }

  getCompetitorsLoading$(): Observable<boolean> {
    return this.store.select(ProductOverviewSelectors.selectCompetitorsLoading);
  }

  getPriceDevelopmentData$(): Observable<PriceDevelopmentChartData | null> {
    return this.store.select(ProductOverviewSelectors.selectPriceDevelopmentData);
  }

  loadPriceDevelopment(productId: string): void {
    this.store.dispatch(ProductOverviewActions.loadPriceDevelopment({ productId }));
  }

  getSalesDevelopmentData$(subMarket: CompetitorSubMarket): Observable<CompetitorChartData | null> {
    return this.store.select(ProductOverviewSelectors.selectSalesDevelopmentData(subMarket));
  }

  loadSalesDevelopment(productId: string, subMarket: CompetitorSubMarket): void {
    this.store.dispatch(ProductOverviewActions.loadSalesDevelopment({ productId, subMarket }));
  }

  loadIpgSummary(productId: string): void {
    this.store.dispatch(ProductOverviewActions.loadIpgSummary({ productId }));
  }

  getIpgSummaryResults$(): Observable<MarketAnalysisResult[] | null> {
    return this.store.select(ProductOverviewSelectors.selectIpgSummaryResults);
  }

  getChartPeriodMonths$(): Observable<number> {
    return this.store.select(ProductOverviewSelectors.selectChartPeriodMonths);
  }

  setChartPeriod(periodMonths: number): void {
    this.store.dispatch(ProductOverviewActions.setChartPeriod({ periodMonths }));
  }

  loadPurchaseProducts(productId: string, subMarket: CompetitorSubMarket): void {
    this.store.dispatch(ProductOverviewActions.loadPurchaseProducts({ productId, subMarket }));
  }

  getPurchaseProducts$(subMarket: CompetitorSubMarket): Observable<PurchaseProduct[]> {
    return this.store.select(ProductOverviewSelectors.selectPurchaseProducts(subMarket));
  }

  // Keeps the notes badge in sync after notes are added or deleted, without reloading the table.
  setPurchaseProductNotesCount(productId: string, notesCount: number): void {
    this.store.dispatch(
      ProductOverviewActions.setPurchaseProductNotesCount({ productId, notesCount })
    );
  }

  changePurchaseProductLinks(
    productId: string,
    updates: LinkStateUpdate[],
    deletes: LinkKey[]
  ): void {
    this.store.dispatch(
      ProductOverviewActions.changePurchaseProductLinks({ productId, updates, deletes })
    );
  }

  loadProduct(productId: string): void {
    this.store.dispatch(ProductOverviewActions.loadProduct({ productId }));
  }

  getDrugId$(): Observable<string | null> {
    return this.store.select(ProductOverviewSelectors.selectDrugId);
  }

  getProductCountryCode$(): Observable<string | null> {
    return this.store.select(ProductOverviewSelectors.selectProductCountryCode);
  }

  // Derived from the country code rather than stored, as the currency can't be changed yet.
  getProductCurrencyCode$(): Observable<string> {
    return this.getProductCountryCode$().pipe(
      switchMap((countryCode: string | null) => this.countryService.getCurrencyCode$(countryCode))
    );
  }

  loadAnalysisSettings(productId: string): void {
    this.store.dispatch(ProductOverviewActions.loadAnalysisSettings({ productId }));
  }

  getMarketShares$(): Observable<MarketShares | null> {
    return this.store.select(ProductOverviewSelectors.selectMarketShares);
  }

  saveAnalysisSettings(settings: UpdateProductAnalysisSettings): void {
    this.store.dispatch(ProductOverviewActions.saveAnalysisSettings({ settings }));
  }

  saveHospitalSettings(settings: UpdateHospitalProductAnalysisSettings): void {
    this.store.dispatch(ProductOverviewActions.saveHospitalSettings({ settings }));
  }

  // Called from product-overview.effects.ts.
  getCompetitors(productId: string): Observable<Competitor[]> {
    const params: HttpParams = new HttpParams().set('ProductId', productId);

    return this.http.get<Competitor[]>(`${environment.apiUrl}${this.competitorsEndpoint}`, {
      params,
    });
  }

  // Called from product-overview.effects.ts.
  getPriceDevelopment(
    productId: string,
    periodMonths: number
  ): Observable<PriceDevelopmentChartData> {
    const params: HttpParams = this.buildChartParams(productId, periodMonths).set(
      'PriceTypeId',
      DEFAULT_PRICE_TYPE_ID
    );

    return this.http.get<PriceDevelopmentChartData>(
      `${environment.apiUrl}${this.competitorsEndpoint}/PriceDevelopment`,
      { params }
    );
  }

  // Called from product-overview.effects.ts.
  getSalesDevelopment(
    productId: string,
    subMarket: CompetitorSubMarket,
    periodMonths: number
  ): Observable<CompetitorChartData> {
    const params: HttpParams = this.buildChartParams(productId, periodMonths)
      .set('SubMarket', subMarket)
      .set('PeriodType', DEFAULT_PERIOD_TYPE);

    return this.http.get<CompetitorChartData>(
      `${environment.apiUrl}${this.competitorsEndpoint}/SalesDevelopment`,
      { params }
    );
  }

  // Called from product-overview.effects.ts.
  getPurchaseProducts(
    productId: string,
    subMarket: CompetitorSubMarket
  ): Observable<PurchaseProduct[]> {
    const params: HttpParams = new HttpParams()
      .set('ProductId', productId)
      .set('Currency', DEFAULT_CURRENCY);

    return this.http.get<PurchaseProduct[]>(
      `${environment.apiUrl}${this.purchaseProductsEndpoint}/${PURCHASE_PRODUCTS_PATHS[subMarket]}`,
      { params }
    );
  }

  // Params shared by all chart endpoints, covering `periodMonths` back from today.
  private buildChartParams(productId: string, periodMonths: number): HttpParams {
    const today: Date = new Date();
    return new HttpParams()
      .set('ReferenceProductId', productId)
      .set('SearchLevel', DrugSearchLevel.InterchangePackSizeGroup)
      .set('FromDate', format(subMonths(today, periodMonths), 'yyyy-MM-dd'))
      .set('ToDate', format(today, 'yyyy-MM-dd'));
  }

  getPurchaseTestCalculation$(
    purchaseTestParams: PurchaseTestParams
  ): Observable<PurchaseProductTestResult> {
    const params: HttpParams = new HttpParams({
      fromObject: {
        ProductId: purchaseTestParams.productId,
        ProductPrice: purchaseTestParams.productPrice,
        PackSizeA: purchaseTestParams.packSizeA,
        PackSizeB: purchaseTestParams.packSizeB,
        ProductPriceCurrency: purchaseTestParams.currency,
        CalculatedCurrency: purchaseTestParams.currency,
      },
    });

    return this.http.get<PurchaseProductTestResult>(
      `${environment.apiUrl}${this.purchaseProductsEndpoint}/test`,
      {
        params,
      }
    );
  }
}
